import { cloneDeep } from 'lodash';
import React from 'react';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { IProposalCover } from './form';
import {
  IProposalExpatDetails,
  IProposalPageFormState,
} from '../../page/expat/form';
import { validateExpatDetailsField } from '../../page/expat/validation';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';

interface IProposalExpatDetailsWidgetProps {
  pageState: IProposalPageFormState;
  onPageStateUpdate: (pageState: IProposalPageFormState) => void;
  disabledForm?: boolean;
  lovs: Record<string, Record<string, string>>;
  defaultPlanCoversList: Record<string, IProposalCover>;
  allowPlanCoverEdits: boolean;
  status: string;
}

const ProposalExpatDetailsWidget: React.FC<
  IProposalExpatDetailsWidgetProps
> = ({ pageState, onPageStateUpdate, disabledForm, lovs }) => {
  const values = pageState.values.expatDetails;
  const errors = pageState.errors.expatDetails;
  const touched = pageState.touched.expatDetails;

  const onFieldBlur = async (fieldName: string) => {
    const newPageState = cloneDeep(pageState);
    newPageState.touched.expatDetails[fieldName] = true;

    onPageStateUpdate(newPageState);
  };

  const onFieldUpdate = (
    fieldName: keyof IProposalExpatDetails,
    value: IProposalExpatDetails[keyof IProposalExpatDetails],
    touched = false
  ) => {
    const newPageState = cloneDeep(pageState);

    newPageState.values.expatDetails[fieldName] = value;

    newPageState.errors.expatDetails[fieldName] = validateExpatDetailsField(
      fieldName,
      value
    );

    if (touched) {
      newPageState.touched.costs[fieldName] = true;
    }

    onPageStateUpdate(newPageState);
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Expat Details" isFieldsGrid>
        <SelectFormField
          key="medicalClass"
          name="medicalClass"
          title="Medical Class*"
          placeholder="Medical Class"
          value={values.medicalClass}
          error={touched.medicalClass ? errors.medicalClass : ''}
          onChange={(v) => onFieldUpdate('medicalClass', v, true)}
          disabled={disabledForm}
          selectOptions={lovs.medicalClasses}
          material
        />
        <SelectFormField
          key="emergency"
          name="emergency"
          title="Emergency*"
          placeholder="Emergency"
          value={values.emergency}
          error={touched.emergency ? errors.emergency : ''}
          onChange={(v) => onFieldUpdate('emergency', v, true)}
          disabled={disabledForm}
          selectOptions={lovs.emergencyOptions}
          material
        />
        <SelectFormField
          key="continuity"
          name="continuity"
          title="Continuity"
          placeholder="Continuity"
          value={values.continuity}
          error={touched.usageType ? errors.continuity : ''}
          onChange={(v) => onFieldUpdate('continuity', v, true)}
          disabled={disabledForm}
          selectOptions={lovs.continuityOptions}
          material
        />

        <TextInputFormField
          key="continuityNumber"
          name="continuityNumber"
          title="Continuity Number"
          placeholder="Continuity Number"
          value={values.continuityNumber}
          error={touched.continuityNumber ? errors.continuityNumber : ''}
          onBlur={() => onFieldBlur('continuityNumber')}
          onChange={(v) => onFieldUpdate('continuityNumber', v.target.value)}
          disabled={disabledForm}
          material
        />

        <TextInputFormField
          key="exclusion"
          name="exclusion"
          title="Exclusion"
          placeholder="Exclusion"
          value={values.exclusion}
          error={touched.exclusion ? errors.exclusion : ''}
          onBlur={() => onFieldBlur('exclusion')}
          onChange={(v) => onFieldUpdate('exclusion', v.target.value)}
          disabled={disabledForm}
          material
          fullWidth
        />

        <TextInputFormField
          key="remarks"
          name="remarks"
          title="Remarks"
          placeholder="Remarks"
          value={values.remarks}
          error={touched.remarks ? errors.remarks : ''}
          onBlur={() => onFieldBlur('remarks')}
          onChange={(v) => onFieldUpdate('remarks', v.target.value)}
          disabled={disabledForm}
          material
          fullWidth
        />
      </WidgetSection>
    </WidgetPaper>
  );
};

export default ProposalExpatDetailsWidget;
